// Sidebar DOM builders: the fractal dropdown and the dynamic extra-param
// sliders. Reads FORMULAS for what to build and state (from viewState.mjs)
// for current values; writes back into state.extra on input.

import { FORMULAS } from "../fractals/formulas.mjs";
import { state } from "./viewState.mjs";

/**
 * Populates `formulaSelect` with one <option> per entry in FORMULAS, skipping
 * `hidden` ones (f64-precision variants — reached only via the precision
 * toggle, see syncPrecisionControl below, never selected directly).
 */
export function buildFormulaOptions(formulaSelect) {
  for (const [name, formula] of Object.entries(FORMULAS)) {
    if (formula.hidden) continue;
    const option = document.createElement("option");
    option.value = name;
    option.textContent = formula.label;
    formulaSelect.appendChild(option);
  }
}

/**
 * Enables/checks the precision-toggle checkbox for the current formula: only
 * enabled when it has an f64Variant (see formulas.mjs), and forced off (both
 * the checkbox and `state.precisionF64` itself) otherwise, so switching to a
 * fractal without one doesn't leave the toggle silently checked-but-inert.
 * @param {HTMLInputElement} checkboxEl
 * @param {HTMLElement} hintEl
 */
export function syncPrecisionControl(checkboxEl, hintEl) {
  const formula = FORMULAS[state.formulaName];
  const available = Boolean(formula.f64Variant);

  if (!available) state.precisionF64 = false;

  checkboxEl.disabled = !available;
  checkboxEl.checked = state.precisionF64;
  hintEl.textContent = available
    ? "WebGPU has no native f64, so this emulates it with two f32s. Slower, but zooms past where f32 pixelates."
    : "Not available for this fractal yet.";
}

/**
 * Rebuilds `extraParamsEl` with one slider per the current formula's
 * extraParams. The extra-param controls differ per formula (Julia's c,
 * Multibrot's degree, …), so this section is generated from
 * FORMULAS.extraParams each time the formula changes rather than hand-coded
 * per fractal.
 * @param {HTMLElement} extraParamsEl
 * @param {() => void} onChange - called after each slider edit (e.g. queueRender)
 */
export function buildExtraParamControls(extraParamsEl, onChange) {
  extraParamsEl.innerHTML = "";
  const formula = FORMULAS[state.formulaName];

  for (const p of formula.extraParams) {
    const row = document.createElement("label");
    row.className = "control-row";

    const span = document.createElement("span");
    span.textContent = p.label;

    const input = document.createElement("input");
    input.type = "range";
    input.min = String(p.min);
    input.max = String(p.max);
    input.step = String(p.step);
    input.value = String(state.extra[p.name]);

    const valueEl = document.createElement("span");
    valueEl.className = "control-value";
    valueEl.textContent = formatExtraValue(state.extra[p.name], p.step);

    input.addEventListener("input", () => {
      const value = Number(input.value);
      state.extra[p.name] = value;
      valueEl.textContent = formatExtraValue(value, p.step);
      onChange();
    });

    row.append(span, input, valueEl);
    extraParamsEl.appendChild(row);
  }
}

function formatExtraValue(value, step) {
  return step < 1 ? value.toFixed(3) : String(value);
}
